import { useMemo } from "react";
import { useQuery } from "react-query";

import { DealersService } from "~/entity/Dealers/api/service";
import { IDealerListItem } from "~/entity/Dealers/model/schema";

const PAGE_SIZE = 100;

const GEO_FIELD_BY_FILTER: Record<string, keyof IDealerListItem> = {
  city: "city",
  region: "subject",
  country: "country",
  federalDistrict: "fo",
};

export const isGeoFilterBy = (filterBy?: string): boolean =>
  Boolean(filterBy && GEO_FIELD_BY_FILTER[filterBy]);

const fetchAllActiveDealers = async (): Promise<IDealerListItem[]> => {
  const first = await DealersService.GET.getDealerList({
    page: 0,
    count: PAGE_SIZE,
    status: "active",
  });
  const rows = [...first.data.dealers];
  const pageCount = Math.ceil(first.data.totalCount / PAGE_SIZE);

  if (pageCount <= 1) {
    return rows;
  }

  const rest = [];

  for (let page = 1; page < pageCount; page += 1) {
    rest.push(
      DealersService.GET.getDealerList({
        page,
        count: PAGE_SIZE,
        status: "active",
      }),
    );
  }

  const responses = await Promise.all(rest);

  responses.forEach((response) => {
    rows.push(...response.data.dealers);
  });

  return rows;
};

export const useBaseStaffGeoFilterOptions = (filterBy?: string) => {
  const query = useQuery({
    queryKey: ["baseStaffGeoFilterDealers"],
    queryFn: fetchAllActiveDealers,
    enabled: isGeoFilterBy(filterBy),
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(() => {
    const field = filterBy ? GEO_FIELD_BY_FILTER[filterBy] : undefined;

    if (!field) {
      return [];
    }

    const values = (query.data || [])
      .map((dealer) => String(dealer[field] || ""))
      .filter(Boolean)
      .filter((value, index, list) => list.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b));

    return values.map((name) => ({ value: name, label: name }));
  }, [query.data, filterBy]);

  return { options, loading: query.isLoading };
};
